import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../../services/api";

const AuthContext = createContext(null);

// Decode JWT token to extract payload
const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

// Check if token is expired
const isTokenExpired = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  return decoded.exp * 1000 < Date.now();
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [scope, setScope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Fetch current user's scope from server
  const fetchUserScope = useCallback(async () => {
    try {
      const response = await api.getCurrentUser();
      if (response.user && response.scope) {
        setUser(prev => ({
          ...prev,
          ...response.user,
          id: response.user._id,
        }));
        setScope(response.scope);
      }
    } catch (error) {
      console.error("Failed to fetch user scope:", error);
    }
  }, []);

  useEffect(() => {
    // Check for existing session
    const token = localStorage.getItem("authToken");

    if (token && !isTokenExpired(token)) {
      const decoded = decodeToken(token);
      if (decoded) {
        const userData = {
          id: decoded.userId,
          username: decoded.username,
          role: decoded.role,
          managerScope: decoded.managerScope || null,
          assignedLocations: decoded.assignedLocations || [],
          assignedWarehouses: decoded.assignedWarehouses || [],
          assignedRooms: decoded.assignedRooms || [],
        };
        setUser(userData);
        setIsAuthenticated(true);
        // Fetch full scope from server
        fetchUserScope();
      }
    } else if (token) {
      // Token expired, clear storage
      localStorage.removeItem("authToken");
    }
    setLoading(false);
  }, [fetchUserScope]);

  const login = async (username, password) => {
    const response = await api.login(username, password);
    if (response.token && response.user) {
      const userData = {
        id: response.user._id,
        username: response.user.username,
        role: response.user.role,
        managerScope: response.user.managerScope || null,
        assignedLocations: response.user.assignedLocations || [],
        assignedWarehouses: response.user.assignedWarehouses || [],
        assignedRooms: response.user.assignedRooms || [],
      };
      setUser(userData);
      setIsAuthenticated(true);
      // Fetch full scope from server
      await fetchUserScope();
      return response;
    }
    throw new Error("Invalid response from server");
  };

  const logout = () => {
    api.logout();
    setUser(null);
    setScope(null);
    setIsAuthenticated(false);
  };

  // Role checks
  const isAdmin = () => user?.role === "admin";
  const isOperator = () => user?.role === "operator"; // For future operator role
  const isCashier = () => user?.role === "cashier";
  const isManager = () => user?.role === "manager";
  const isWorker = () => user?.role === "worker";
  const isViewer = () => user?.role === "viewer";
  const isManagerOrAdmin = () => isAdmin() || isManager();
  const isWorkerOrAbove = () => isAdmin() || isManager() || isWorker();

  // Manager scope checks
  const isLocationManager = () => isManager() && user?.managerScope === "location";
  const isWarehouseManager = () => isManager() && user?.managerScope === "warehouse";

  // Helper to get ID from object or string
  const getId = (item) => {
    if (!item) return null;
    if (typeof item === "string") return item;
    return item._id || item.id;
  };

  // Access checks using computed scope from server
  const hasAccessToLocation = (locationId) => {
    if (isAdmin() || isOperator()) return true;
    if (scope?.isAdmin) return true;
    if (scope?.locationIds === '*') return true;

    const id = getId(locationId);
    return scope?.locationIds?.includes(id) || user?.assignedLocations?.some(loc => getId(loc) === id);
  };

  const hasAccessToWarehouse = (warehouseId, warehouseLocationId = null) => {
    if (isAdmin() || isOperator()) return true;
    if (scope?.isAdmin) return true;
    if (scope?.warehouseIds === '*') return true;

    const id = getId(warehouseId);

    // Check computed scope first
    if (scope?.warehouseIds?.includes(id)) return true;

    // Direct warehouse assignment
    if (user?.assignedWarehouses?.some(wh => getId(wh) === id)) {
      return true;
    }

    // Check if warehouse's location is assigned
    if (warehouseLocationId && hasAccessToLocation(warehouseLocationId)) {
      return true;
    }

    return false;
  };

  const hasAccessToRoom = (roomId, roomWarehouseId = null, roomLocationId = null) => {
    if (isAdmin() || isOperator()) return true;
    if (scope?.isAdmin) return true;
    if (scope?.roomIds === '*') return true;

    const id = typeof roomId === 'number' ? roomId : parseInt(getId(roomId));

    // Check computed scope first
    if (scope?.roomIds?.includes(id)) return true;

    // Direct room assignment (rooms are stored as numeric rids)
    if (user?.assignedRooms?.includes(id)) {
      return true;
    }

    // Check if room's warehouse is assigned
    if (roomWarehouseId && hasAccessToWarehouse(roomWarehouseId, roomLocationId)) {
      return true;
    }

    return false;
  };

  // Permission checks for specific operations
  const canCreateLocation = () => isAdmin();

  const canCreateWarehouse = () => {
    if (isAdmin()) return true;
    return isLocationManager(); // Location-scoped managers can create warehouses
  };

  const canCreateRoom = () => {
    if (isAdmin()) return true;
    return isManager(); // All managers can create rooms in their scope
  };

  const canManageTags = (roomId = null) => {
    if (isAdmin() || isManager()) return true;

    // Workers can manage tags in their assigned rooms
    if (isWorker()) {
      if (roomId && hasAccessToRoom(roomId)) return true;
      return false;
    }

    // Viewers cannot manage tags
    return false;
  };

  const canViewTags = (roomId = null) => {
    if (isAdmin() || isOperator() || isCashier()) return true;
    if (roomId && hasAccessToRoom(roomId)) return true;
    return false;
  };

  const canManageUsers = () => {
    // Admins and managers can manage users
    return isAdmin() || isManager();
  };

  const canCreateUser = (targetRole) => {
    if (isAdmin()) return true;

    // Managers can only create workers and viewers
    if (isManager()) {
      return ['worker', 'viewer'].includes(targetRole);
    }

    return false;
  };

  const value = {
    user,
    scope,
    loading,
    isAuthenticated,
    login,
    logout,
    // Role checks
    isOperator,
    isCashier,
    isAdmin,
    isManager,
    isWorker,
    isViewer,
    isManagerOrAdmin,
    isWorkerOrAbove,
    isLocationManager,
    isWarehouseManager,
    // Access checks
    hasAccessToLocation,
    hasAccessToWarehouse,
    hasAccessToRoom,
    // Permission checks
    canCreateLocation,
    canCreateWarehouse,
    canCreateRoom,
    canManageTags,
    canViewTags,
    canManageUsers,
    canCreateUser,
    // Utility
    fetchUserScope,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
